import { clientStorage } from "@/lib/clientStorage";
import React, { useState, useEffect } from "react";
import ActionstepSavePanel from "@/components/ActionstepSavePanel";
import { actionstepFolderFor } from "@/lib/actionstepFolders";
import { createPortalPdf, portalDocumentName } from "@/lib/portalPdf";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Shield, Upload, X, Loader2, AlertTriangle, CheckCircle2, AlertCircle, FileText, Link2, RefreshCw, Database, Trash2, Printer, Download, History, Mail } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { INTAKE_EMAIL } from "@/lib/demoConfig";

const isNameMatch = (extracted, db) => {
  const ext = (extracted || "").toLowerCase().trim();
  const database = (db || "").toLowerCase().trim();
  
  if (!ext || !database) return false;

  // 1. Exact match after basic normalization (handles spaces, hyphens, punctuation)
  const extNorm = ext.replace(/-/g, " ").replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ");
  const dbNorm = database.replace(/-/g, " ").replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ");
  
  if (extNorm.includes(dbNorm) || dbNorm.includes(extNorm)) {
    return true;
  }

  // 2. Letters-only match (handles WILSONSMITH vs WILSON-SMITH or missing hyphens/spaces)
  const extLetters = ext.replace(/[^a-z0-9]/g, "");
  const dbLetters = database.replace(/[^a-z0-9]/g, "");
  if (dbLetters.length >= 4 && (extLetters.includes(dbLetters) || dbLetters.includes(extLetters))) {
    return true;
  }

  // 3. Token-based match: all tokens in the database name must be present in the extracted name.
  // We ignore very short tokens (like initials "a", "j") to avoid false positives.
  const dbTokens = dbNorm.split(" ").filter(t => t.length > 1);
  if (dbTokens.length >= 2) {
    const allTokensMatch = dbTokens.every(token => extNorm.includes(token));
    if (allTokensMatch) return true;
  }

  return false;
};

const excelSerialToDateString = (val) => {
  if (val === undefined || val === null || val === "") return "";
  const str = String(val).trim();
  // Check if it is a pure 5-digit number representing an Excel serial date
  if (/^\d{5}$/.test(str)) {
    const num = Number(str);
    if (num >= 10000 && num <= 60000) {
      try {
        const utc_days = Math.floor(num - 25569);
        const date_info = new Date(utc_days * 86400 * 1000);
        const day = String(date_info.getDate()).padStart(2, '0');
        const month = String(date_info.getMonth() + 1).padStart(2, '0');
        const year = date_info.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        console.error("Error converting excel serial date", e);
      }
    }
  }
  return str;
};

const parseDateParts = (str) => {
  const digits = str.replace(/[^0-9]/g, "");
  if (digits.length === 8) {
    if (digits.startsWith("19") || digits.startsWith("20")) {
      // YYYYMMDD
      return {
        d: parseInt(digits.substring(6, 8)),
        m: parseInt(digits.substring(4, 6)),
        y: parseInt(digits.substring(0, 4))
      };
    } else {
      // DDMMYYYY
      return {
        d: parseInt(digits.substring(0, 2)),
        m: parseInt(digits.substring(2, 4)),
        y: parseInt(digits.substring(4, 8))
      };
    }
  } else if (digits.length === 6) {
    // DDMMYY
    const yy = parseInt(digits.substring(4, 6));
    const year = yy < 50 ? 2000 + yy : 1900 + yy;
    return {
      d: parseInt(digits.substring(0, 2)),
      m: parseInt(digits.substring(2, 4)),
      y: year
    };
  }
  return null;
};

const isDobMatch = (entered, dbDob) => {
  const entNorm = (entered || "").trim().replace(/[^0-9]/g, "");
  const dbNorm = (dbDob || "").trim().replace(/[^0-9]/g, "");
  if (!entNorm || !dbNorm) return false;

  if (entNorm === dbNorm) return true;

  const p1 = parseDateParts(entNorm);
  const p2 = parseDateParts(dbNorm);

  if (p1 && p2) {
    return p1.d === p2.d && p1.m === p2.m && (p1.y === p2.y || (p1.y % 100) === (p2.y % 100));
  }

  return false;
};

export default function ConflictCheck({ isPortalMode = false, brandType = "vals" }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [conflictFiles, setConflictFiles] = useState([]);
  const [conflictLoading, setConflictLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState("");
  const [extractedNames, setExtractedNames] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [scanError, setScanError] = useState("");
  const [showActiveNames, setShowActiveNames] = useState(false);
  const [scanCompletedAt, setScanCompletedAt] = useState("");
  const [savedChecks, setSavedChecks] = useState([]);
  const [enteredDobs, setEnteredDobs] = useState({});
  const [currentCheckId, setCurrentCheckId] = useState(null);

  // Email dialog states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailing, setEmailing] = useState(false);

  // Linked Document States
  const [linkedUrl, setLinkedUrl] = useState(() => clientStorage.getItem("demo_conflict_link") || "");
  const [isLinked, setIsLinked] = useState(!!clientStorage.getItem("demo_conflict_link"));
  const [lastSynced, setLastSynced] = useState(() => clientStorage.getItem("demo_conflict_last_synced") || "");
  const [isSyncing, setIsSyncing] = useState(false);
  const [linkInput, setLinkInput] = useState(linkedUrl);

  const { data: conflictNames, refetch: refetchConflictNames } = useQuery({
    queryKey: ["conflictNames"],
    queryFn: () => base44.entities.ConflictName.list("name", 500),
    initialData: [],
  });

  // Load saved checks from history and listen for storage changes
  useEffect(() => {
    const loadSavedChecks = () => {
      const brandKey = brandType ? `${brandType}_` : "";
      const key = `${brandKey}conflict_checks_list`;
      const savedStr = clientStorage.getItem(key);
      if (savedStr) {
        try {
          setSavedChecks(JSON.parse(savedStr));
        } catch (e) {
          console.error(e);
        }
      } else {
        setSavedChecks([]);
      }
    };
    loadSavedChecks();
    
    const handleStorageChange = () => {
      refetchConflictNames();
      loadSavedChecks();
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [brandType, refetchConflictNames]);

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];
    
    // Find name column index
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    // Check if there are separate first and last name columns
    let firstNameIndex = headers.findIndex(h => h.includes("first name") || h.includes("first_name") || h.includes("firstname") || h.includes("given name") || h.includes("given_name") || h.includes("givenname") || h.includes("forename"));
    let lastNameIndex = headers.findIndex(h => h.includes("last name") || h.includes("last_name") || h.includes("lastname") || h.includes("surname") || h.includes("family name") || h.includes("family_name") || h.includes("familyname"));
    let dobIndex = headers.findIndex(h => h.includes("dob") || h.includes("d.o.b") || h.includes("date of birth") || h.includes("birthdate") || h.includes("birth date") || h.includes("birth_date"));
    
    let nameIndex = headers.findIndex(h => h.includes('name') || h.includes('client') || h.includes('party'));
    if (nameIndex === -1 && firstNameIndex === -1) nameIndex = 0; // Fallback to first column
    
    const namesList = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const columns = line.split(',');
      let fullName = "";
      
      if (firstNameIndex !== -1 && lastNameIndex !== -1) {
        const firstVal = (columns[firstNameIndex] || "").trim().replace(/^["']|["']$/g, '');
        const lastVal = (columns[lastNameIndex] || "").trim().replace(/^["']|["']$/g, '');
        if (firstVal || lastVal) {
          fullName = `${firstVal} ${lastVal}`.trim();
        }
      } else {
        const nameVal = columns[nameIndex]?.trim()?.replace(/^["']|["']$/g, '');
        if (nameVal) {
          fullName = nameVal;
        }
      }

      let dobVal = "";
      if (dobIndex !== -1) {
        const rawDob = (columns[dobIndex] || "").trim().replace(/^["']|["']$/g, '');
        dobVal = excelSerialToDateString(rawDob);
      }

      if (fullName) {
        namesList.push({
          id: `imported-${Date.now()}-${i}`,
          name: fullName,
          dob: dobVal,
          category: "Uploaded Client List"
        });
      }
    }
    return namesList;
  };

  const parseJSON = (text) => {
    try {
      const parsed = JSON.parse(text);
      const namesList = [];
      const items = Array.isArray(parsed) ? parsed : [parsed];
      
      items.forEach((item, idx) => {
        if (typeof item === 'string') {
          namesList.push({
            id: `imported-${Date.now()}-${idx}`,
            name: item,
            dob: "",
            category: "Uploaded Client List"
          });
        } else if (item && typeof item === 'object') {
          const nameKey = Object.keys(item).find(k => k.toLowerCase().includes('name') || k.toLowerCase().includes('client'));
          const val = item[nameKey || Object.keys(item)[0]];
          
          const dobKey = Object.keys(item).find(k => {
            const kl = k.toLowerCase();
            return kl.includes("dob") || kl.includes("d.o.b") || kl.includes("date of birth") || kl.includes("birthdate") || kl.includes("birth date") || kl.includes("birth_date");
          });
          const dobVal = dobKey ? excelSerialToDateString(String(item[dobKey]).trim()) : "";

          if (val) {
            namesList.push({
              id: `imported-${Date.now()}-${idx}`,
              name: String(val),
              dob: dobVal,
              category: "Uploaded Client List"
            });
          }
        }
      });
      return namesList;
    } catch (e) {
      toast.error("Failed to parse JSON file.");
      return [];
    }
  };

  const parseXLSX = (arrayBuffer) => {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      
      const namesList = [];
      jsonData.forEach((row, idx) => {
        if (!row) return;
        const keys = Object.keys(row);
        
        // Find first name and last name keys
        const firstNameKey = keys.find(k => {
          const kl = k.toLowerCase();
          return kl.includes("first name") || kl.includes("first_name") || kl.includes("firstname") || kl.includes("given name") || kl.includes("given_name") || kl.includes("givenname") || kl.includes("forename");
        });
        
        const lastNameKey = keys.find(k => {
          const kl = k.toLowerCase();
          return kl.includes("last name") || kl.includes("last_name") || kl.includes("lastname") || kl.includes("surname") || kl.includes("family name") || kl.includes("family_name") || kl.includes("familyname");
        });

        let fullName = "";
        
        if (firstNameKey && lastNameKey) {
          const firstVal = String(row[firstNameKey] || "").trim();
          const lastVal = String(row[lastNameKey] || "").trim();
          if (firstVal || lastVal) {
            fullName = `${firstVal} ${lastVal}`.trim();
          }
        } else {
          // Fallback to name/client key or first column
          const nameKey = keys.find(k => {
            const kl = k.toLowerCase();
            return kl.includes('name') || kl.includes('client') || kl.includes('party');
          });
          const val = nameKey ? row[nameKey] : row[keys[0]];
          if (val) {
            fullName = String(val).trim();
          }
        }

        const dobKey = keys.find(k => {
          const kl = k.toLowerCase();
          return kl.includes("dob") || kl.includes("d.o.b") || kl.includes("date of birth") || kl.includes("birthdate") || kl.includes("birth date") || kl.includes("birth_date");
        });
        const dobVal = dobKey ? excelSerialToDateString(String(row[dobKey]).trim()) : "";

        if (fullName) {
          namesList.push({
            id: `imported-${Date.now()}-${idx}`,
            name: fullName,
            dob: dobVal,
            category: "Uploaded Client List"
          });
        }
      });
      return namesList;
    } catch (e) {
      console.error("Failed to parse Excel file", e);
      toast.error("Failed to parse Excel file.");
      return [];
    }
  };

  const handleClientListUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      let importedNames = [];
      
      if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        const arrayBuffer = event.target?.result;
        if (arrayBuffer) {
          importedNames = parseXLSX(arrayBuffer);
        }
      } else {
        const text = event.target?.result;
        if (typeof text === "string") {
          if (file.name.endsWith(".json")) {
            importedNames = parseJSON(text);
          } else {
            importedNames = parseCSV(text);
          }
        }
      }

      if (importedNames.length > 0) {
        clientStorage.setItem("demo_conflict_names", JSON.stringify(importedNames));
        window.dispatchEvent(new Event("storage"));
        queryClient.invalidateQueries(["conflictNames"]);
        refetchConflictNames();
        toast.success(`Successfully uploaded and loaded ${importedNames.length} names into client list database.`);
      } else {
        toast.error("No names could be extracted from this file. Ensure it contains a 'name' or 'client' column.");
      }
    };

    if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const handleLinkDocument = async (e) => {
    e.preventDefault();
    if (!linkInput.trim()) {
      toast.error("Please enter a link URL.");
      return;
    }
    await syncLinkedDocument(linkInput.trim());
  };

  const syncLinkedDocument = async (urlToSync) => {
    setIsSyncing(true);
    try {
      let dataText = "";
      try {
        const res = await fetch(urlToSync);
        if (res.ok) {
          dataText = await res.text();
        }
      } catch (e) {
        console.warn("Direct conflict-list fetch failed (CORS or network).", e);
      }

      let parsedNames = [];
      if (dataText) {
        if (urlToSync.includes(".json") || dataText.trim().startsWith("[")) {
          parsedNames = parseJSON(dataText);
        } else {
          parsedNames = parseCSV(dataText);
        }
      } else {
        throw new Error("The linked conflict list could not be read. Check that the link is accessible and try again.");
      }

      clientStorage.setItem("demo_conflict_names", JSON.stringify(parsedNames));
      clientStorage.setItem("demo_conflict_link", urlToSync);
      const nowStr = new Date().toLocaleTimeString("en-AU") + " " + new Date().toLocaleDateString("en-AU");
      clientStorage.setItem("demo_conflict_last_synced", nowStr);
      
      setLinkedUrl(urlToSync);
      setIsLinked(true);
      setLastSynced(nowStr);
      
      window.dispatchEvent(new Event("storage"));
      queryClient.invalidateQueries(["conflictNames"]);
      refetchConflictNames();

      toast.success(`Synced live document! Loaded ${parsedNames.length} records.`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to sync live document.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleUnlinkDocument = () => {
    clientStorage.removeItem("demo_conflict_link");
    clientStorage.removeItem("demo_conflict_last_synced");
    clientStorage.setItem("demo_conflict_names", JSON.stringify([]));
    
    setLinkedUrl("");
    setIsLinked(false);
    setLastSynced("");
    setLinkInput("");
    
    window.dispatchEvent(new Event("storage"));
    queryClient.invalidateQueries(["conflictNames"]);
    refetchConflictNames();
    toast.success("Unlinked live document and cleared client list.");
  };

  const handleClearDatabase = () => {
    clientStorage.setItem("demo_conflict_names", JSON.stringify([]));
    window.dispatchEvent(new Event("storage"));
    queryClient.invalidateQueries(["conflictNames"]);
    refetchConflictNames();
    toast.success("Client list database cleared.");
  };

  const handleConflictScan = async () => {
    if (conflictFiles.length === 0) return;
    setConflictLoading(true);
    setScanProgress("Starting scan...");
    setScanError("");
    try {
      const aggregatedExtracted = {
        victims: [],
        witnesses: [],
        other_parties: []
      };

      for (const file of conflictFiles) {
        setScanProgress(`Uploading ${file.name}...`);
        const { file_url } = await base44.integrations.Core.UploadFile({ file });
        
        setScanProgress(`Analyzing ${file.name}...`);
        const result = await base44.integrations.Core.ExtractDataFromUploadedFile({
          file_url,
          json_schema: {
            type: "object",
            properties: {
              victims: {
                type: "array",
                items: { type: "object", properties: { full_name: { type: "string" }, role: { type: "string" } } },
                description: "Victims mentioned in the document"
              },
              witnesses: {
                type: "array",
                items: { type: "object", properties: { full_name: { type: "string" }, role: { type: "string" } } },
                description: "Witnesses mentioned in the document"
              },
              other_parties: {
                type: "array",
                items: { type: "object", properties: { full_name: { type: "string" }, role: { type: "string" } } },
                description: "Other relevant parties (co-accused, informants, etc.)"
              }
            }
          }
        }, (current, total) => {
          setScanProgress(`Running OCR on ${file.name}: Page ${current} of ${total}...`);
        });

        if (result.status === "success" && result.output) {
          ["victims", "witnesses", "other_parties"].forEach(key => {
            (result.output[key] || []).forEach(newItem => {
              if (newItem && newItem.full_name) {
                const exists = aggregatedExtracted[key].some(
                  existing => (existing.full_name || "").toLowerCase() === (newItem.full_name || "").toLowerCase()
                );
                if (!exists) aggregatedExtracted[key].push(newItem);
              }
            });
          });
        }
      }

      setExtractedNames(aggregatedExtracted);
      const allNames = [
        ...aggregatedExtracted.victims,
        ...aggregatedExtracted.witnesses,
        ...aggregatedExtracted.other_parties
      ];

      const foundConflicts = [];
      allNames.forEach((person) => {
        const matches = (conflictNames || []).filter(cn => {
          return isNameMatch(person.full_name, cn.name);
        });
        if (matches.length > 0) {
          foundConflicts.push({ name: person.full_name, role: person.role || "Party", matches });
        }
      });
      setConflicts(foundConflicts);
      
      const completedAtStr = new Date().toLocaleTimeString("en-AU") + " " + new Date().toLocaleDateString("en-AU");
      setScanCompletedAt(completedAtStr);
      setEnteredDobs({});
      
      const checkId = `check-${Date.now()}`;
      setCurrentCheckId(checkId);

      const brandKey = brandType ? `${brandType}_` : "";
      const key = `${brandKey}conflict_checks_list`;
      const newCheck = {
        id: checkId,
        files: conflictFiles.map(f => f.name),
        completed_at: completedAtStr,
        extracted_count: allNames.length,
        conflicts_count: foundConflicts.length,
        extractedNames: aggregatedExtracted,
        conflicts: foundConflicts,
        enteredDobs: {}
      };

      const updatedChecks = [newCheck, ...savedChecks];
      setSavedChecks(updatedChecks);
      clientStorage.setItem(key, JSON.stringify(updatedChecks));

      toast.success("Conflict scan completed successfully!");
    } catch (err) {
      console.error("Conflict scan failed", err);
      setScanError(err instanceof Error ? err.message : "An unexpected error occurred during the conflict check.");
      toast.error("Failed to run conflict scan.");
    } finally {
      setConflictLoading(false);
    }
  };

  const handleConflictReset = () => {
    setConflictFiles([]);
    setExtractedNames(null);
    setConflicts(null);
    setScanError("");
    setEnteredDobs({});
    setScanCompletedAt("");
    setCurrentCheckId(null);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailTo) return;
    setEmailing(true);
    try {
      const emailSubject = `Conflict of Interest Check Report - ${conflictFiles.map(f => f.name).join(", ")}`;
      let emailBody = `CONFLICT OF INTEREST CHECK REPORT\n=================================\n\nDate: ${new Date().toLocaleString("en-AU")}\nFiles Checked:\n`;
      conflictFiles.forEach(f => { emailBody += `  - ${f.name}\n`; });
      emailBody += `\nScan Status: ${conflicts && conflicts.length === 0 ? "NO CONFLICT FOUND" : "POTENTIAL CONFLICTS DETECTED"}\n\n`;

      if (extractedNames) {
        emailBody += `EXTRACTED PARTIES & CONFLICT STATUS:\n------------------------------------\n`;
        const combined = getCombinedExtractedNames();
        combined.forEach((person, idx) => {
          const conflict = getConflictForPerson(person.full_name);
          emailBody += `${idx + 1}. ${person.full_name} (${formatRole(person)})\n`;
          if (conflict) {
            emailBody += `   Status: CONFLICT DETECTED\n`;
            conflict.matches.forEach(m => {
              emailBody += `   - Matches: ${m.name}${m.dob ? ` (DOB: ${m.dob})` : ""} [${m.category || "Client"}]\n`;
            });
          } else {
            emailBody += `   Status: CLEAR / NO CONFLICT\n`;
          }
          emailBody += `\n`;
        });
      }

      if (emailMsg) {
        emailBody += `\nMESSAGE FROM SENDER:\n--------------------\n${emailMsg}`;
      }

      try {
        await base44.integrations.Core.SendEmail({
          to: emailTo,
          cc: emailCc || "",
          subject: emailSubject,
          body: emailBody
        });
      } catch (sendErr) {
        console.warn("SendEmail API failed, falling back to mailto:", sendErr);
      }

      const mailtoUrl = `mailto:${encodeURIComponent(emailTo)}?subject=${encodeURIComponent(emailSubject)}${emailCc ? `&cc=${encodeURIComponent(emailCc)}` : ""}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoUrl;

      toast.success("Email client opened!");
      setShowEmailDialog(false);
      setEmailTo("");
      setEmailCc("");
      setEmailMsg("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send email.");
    } finally {
      setEmailing(false);
    }
  };

  const handleDobChange = (personName, value) => {
    setEnteredDobs(prev => ({
      ...prev,
      [personName]: value
    }));
  };

  const handleLoadSavedCheck = (check) => {
    const fakeFiles = (check.files || []).map(filename => ({
      name: filename,
      size: 0,
    }));
    setConflictFiles(fakeFiles);
    setExtractedNames(check.extractedNames);
    setConflicts(check.conflicts);
    setScanCompletedAt(check.completed_at || "");
    setScanError("");
    setEnteredDobs(check.enteredDobs || {});
    setCurrentCheckId(check.id);
    toast.success("Loaded saved conflict check details.");
  };

  const handleDeleteSavedCheck = (checkId, e) => {
    e.stopPropagation();
    try {
      const brandKey = brandType ? `${brandType}_` : "";
      const key = `${brandKey}conflict_checks_list`;
      const filtered = savedChecks.filter(c => c.id !== checkId);
      clientStorage.setItem(key, JSON.stringify(filtered));
      setSavedChecks(filtered);
      if (currentCheckId === checkId) {
        setCurrentCheckId(null);
      }
      toast.success("Saved check deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete saved check.");
    }
  };

  // Sync changes to enteredDobs back to clientStorage for the current loaded check
  useEffect(() => {
    if (!currentCheckId) return;
    const brandKey = brandType ? `${brandType}_` : "";
    const key = `${brandKey}conflict_checks_list`;
    setSavedChecks(prev => {
      const updated = prev.map(c => {
        if (c.id === currentCheckId) {
          return { ...c, enteredDobs };
        }
        return c;
      });
      const prevString = clientStorage.getItem(key);
      const newString = JSON.stringify(updated);
      if (prevString !== newString) {
        clientStorage.setItem(key, newString);
      }
      return updated;
    });
  }, [enteredDobs, currentCheckId, brandType]);

  const handleAddConflictFiles = (e) => {
    const newFiles = Array.from(e.target.files);
    setConflictFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (indexToRemove) => {
    setConflictFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handlePrint = () => {
    window.print();
  };

  const getCombinedExtractedNames = () => {
    if (!extractedNames) return [];
    const list = [];
    (extractedNames.victims || []).forEach(v => {
      list.push({ ...v, category: "Victim" });
    });
    (extractedNames.witnesses || []).forEach(w => {
      list.push({ ...w, category: "Witness" });
    });
    (extractedNames.other_parties || []).forEach(o => {
      list.push({ ...o, category: "Other Party" });
    });
    return list;
  };

  const getConflictForPerson = (personName) => {
    if (!conflicts) return null;
    return conflicts.find(c => (c.name || "").toLowerCase().trim() === (personName || "").toLowerCase().trim());
  };

  const formatRole = (person) => {
    const role = person.role || person.category || "Party";
    return role.split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
  };

  const getRoleBadgeClass = (person) => {
    const roleLower = (person.role || person.category || "").toLowerCase();
    if (roleLower.includes("victim") || roleLower.includes("complainant")) {
      return "bg-purple-50 text-purple-700 border border-purple-100";
    }
    if (roleLower.includes("police") || roleLower.includes("informant") || roleLower.includes("officer")) {
      return "bg-blue-50 text-blue-700 border border-blue-100";
    }
    if (roleLower.includes("corroborator")) {
      return "bg-indigo-50 text-indigo-700 border border-indigo-100";
    }
    if (roleLower.includes("accused")) {
      return "bg-amber-50 text-amber-700 border border-amber-100";
    }
    return "bg-slate-100 text-slate-700 border border-slate-200";
  };

  const handleDownloadReport = () => {
    if (conflicts === null) return;
    
    let reportText = `JARROD BEHAN LAW\n`;
    reportText += `CONFLICT OF INTEREST CHECK REPORT\n`;
    reportText += `=================================\n\n`;
    reportText += `Date: ${new Date().toLocaleString("en-AU")}\n`;
    reportText += `Performed By: Jarrod Behan Law Portal\n`;
    reportText += `Checked Files:\n`;
    conflictFiles.forEach(f => {
      reportText += `  - ${f.name} (${(f.size / 1024).toFixed(1)} KB)\n`;
    });
    reportText += `\n`;
    
    reportText += `SCAN STATUS: ${conflicts.length === 0 ? "NO CONFLICT FOUND" : "POTENTIAL CONFLICTS DETECTED"}\n`;
    reportText += `--------------------------------------------------\n\n`;
    
    if (extractedNames) {
      reportText += `EXTRACTED PARTIES & CONFLICT STATUS:\n`;
      reportText += `------------------------------------\n`;
      
      const combined = getCombinedExtractedNames();
      combined.forEach((person, idx) => {
        const cleanRole = formatRole(person);
        const conflict = getConflictForPerson(person.full_name);
        
        reportText += `${idx + 1}. Name: ${person.full_name}\n`;
        reportText += `   Role/Type: ${cleanRole}\n`;
        if (conflict) {
          reportText += `   Status: CONFLICT DETECTED - DOB required for final check\n`;
          reportText += `   Matches:\n`;
          conflict.matches.forEach(m => {
            reportText += `     - ${m.name} (${m.category || "Client"})\n`;
          });
        } else {
          reportText += `   Status: CLEAR / NO CONFLICT\n`;
        }
        reportText += `\n`;
      });
    } else {
      reportText += `No matching names detected in the active client list database.\n`;
    }
    
    const blob = new Blob([reportText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Conflict_Report_${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Conflict report file downloaded successfully.");
  };

  const brandInfo = {
    eas: {
      name: "EAS Legal",
      logo: "https://easlegal.com.au/wp-content/uploads/2025/11/easlegal-gold-dark@4x-1024x308.png.webp",
    },
    vals: {
      name: "VALS",
      logo: "/vals-logo.png",
    },
    lacw: {
      name: "Law & Advocacy Centre for Women",
      logo: "/lacw-logo.png",
    },
    jbl: {
      name: "Jarrod Behan Law",
      logo: "/jbl-logo.png",
    }
  }[brandType] || {
    name: "Jarrod Behan Law",
    logo: "/jbl-logo.png",
  };

  const getActiveConflictsCount = () => {
    if (!conflicts) return 0;
    let count = 0;
    conflicts.forEach(c => {
      const enteredDob = enteredDobs[c.name] || "";
      let hasDbDobs = false;
      let isDefinitiveConflict = false;

      c.matches.forEach(m => {
        if (m.dob) {
          hasDbDobs = true;
          if (enteredDob && isDobMatch(enteredDob, m.dob)) {
            isDefinitiveConflict = true;
          }
        }
      });

      if (hasDbDobs) {
        if (enteredDob) {
          if (isDefinitiveConflict) {
            count++;
          }
        } else {
          count++;
        }
      } else {
        count++;
      }
    });
    return count;
  };

  const renderConflictStatus = (person, conflict) => {
    if (!conflict) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
          <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
          Clear / No Conflict
        </span>
      );
    }

    const enteredDob = enteredDobs[person.full_name] || "";
    let hasDbDobs = false;
    let isDefinitiveConflict = false;

    conflict.matches.forEach(m => {
      if (m.dob) {
        hasDbDobs = true;
        if (enteredDob && isDobMatch(enteredDob, m.dob)) {
          isDefinitiveConflict = true;
        }
      }
    });

    if (hasDbDobs) {
      if (enteredDob) {
        if (isDefinitiveConflict) {
          return (
            <div className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200 w-fit">
                <AlertCircle className="w-3.5 h-3.5 text-red-600" />
                🚨 Definitive Conflict Match
              </span>
              <span className="text-[10px] text-red-600 font-bold uppercase mt-0.5">
                DOB matches database record
              </span>
            </div>
          );
        } else {
          return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200 w-fit">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
              ✅ Cleared (DOB Mismatch)
            </span>
          );
        }
      } else {
        return (
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100 w-fit">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
              Conflict Detected
            </span>
            <span className="text-[10px] text-amber-600 font-bold tracking-wide uppercase flex items-center gap-1 mt-0.5">
              ⚠️ DOB required for final check
            </span>
          </div>
        );
      }
    } else {
      return (
        <div className="flex flex-col gap-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-100 w-fit">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
            Conflict Detected
          </span>
          <span className="text-[10px] text-slate-500 font-bold tracking-wide uppercase flex items-center gap-1 mt-0.5">
            ⚠️ No DOB in Database (Verify Manually)
          </span>
        </div>
      );
    }
  };

  const renderPrintConflictStatus = (person, conflict) => {
    if (!conflict) {
      return <span className="font-bold text-green-600">CLEAR / NO CONFLICT</span>;
    }

    const enteredDob = enteredDobs[person.full_name] || "";
    let hasDbDobs = false;
    let isDefinitiveConflict = false;

    conflict.matches.forEach(m => {
      if (m.dob) {
        hasDbDobs = true;
        if (enteredDob && isDobMatch(enteredDob, m.dob)) {
          isDefinitiveConflict = true;
        }
      }
    });

    if (hasDbDobs) {
      if (enteredDob) {
        if (isDefinitiveConflict) {
          return (
            <div className="space-y-1">
              <span className="font-bold text-red-600">🚨 DEFINITIVE CONFLICT MATCH</span>
              <div className="text-[10px] text-red-600 font-bold uppercase mt-0.5">DOB matches database record</div>
            </div>
          );
        } else {
          return (
            <div className="space-y-1">
              <span className="font-bold text-green-600">✅ CLEARED (DOB MISMATCH)</span>
              <div className="text-[10px] text-green-600 font-bold uppercase mt-0.5">DOB differs from database record</div>
            </div>
          );
        }
      } else {
        return (
          <div className="space-y-1">
            <span className="font-bold text-red-600">CONFLICT DETECTED</span>
            <div className="text-[10px] text-amber-700 font-bold uppercase tracking-wider mt-0.5">⚠️ DOB required for final check</div>
          </div>
        );
      }
    } else {
      return (
        <div className="space-y-1">
          <span className="font-bold text-red-600">CONFLICT DETECTED</span>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">⚠️ No DOB in Database (Verify Manually)</div>
        </div>
      );
    }
  };

  const primaryColor = brandType === "lacw" ? "#9333ea" : brandType === "jbl" ? "#C29A5B" : brandType === "vals" ? "#8C1D18" : "#C29A5B";
  const hoverColor = brandType === "lacw" ? "#7e22ce" : brandType === "jbl" ? "#b3894b" : brandType === "vals" ? "#721713" : "#b3894b";

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      
      {/* Back to Portal Button */}
      {isPortalMode && (
        <div className="flex justify-start print:hidden">
          <Button
            onClick={() => navigate(`/${brandType}-portal`)}
            variant="outline"
            className="rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-wide border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
          >
            ← Back to Portal
          </Button>
        </div>
      )}
      
      {/* Configuration & Linking Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-6 print:hidden">
        
        {/* Left column: Stats & Direct Import */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
            <Database className="w-4 h-4" style={{ color: primaryColor }} />
            Client list Database
          </h3>
          <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between border border-slate-100">
            <div>
              <p className="text-2xl font-bold text-slate-800">{conflictNames.length}</p>
              <p className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Records in active database</p>
            </div>
            {conflictNames.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClearDatabase}
                className="text-slate-400 hover:text-red-500 text-xs flex items-center gap-1 hover:bg-transparent"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </Button>
            )}
          </div>

          {conflictNames.length > 0 && (
            <div className="pl-1">
              <button
                type="button"
                onClick={() => setShowActiveNames(!showActiveNames)}
                className="text-xs font-bold hover:underline flex items-center gap-1 transition-all"
                style={{ color: primaryColor }}
              >
                {showActiveNames ? "Hide active names" : "Show active names"}
              </button>
              {showActiveNames && (
                <div className="mt-2 max-h-40 overflow-y-auto border border-slate-100 rounded-xl p-3 bg-slate-50/50 space-y-1.5 scrollbar-thin">
                  {conflictNames.map((cn, i) => (
                    <div key={cn.id || i} className="flex justify-between items-center py-1.5 border-b border-slate-100/50 last:border-0 text-xs">
                      <span className="font-semibold text-slate-800">{cn.name}</span>
                      <span className="text-[10px] text-slate-400 bg-white px-2 py-0.5 rounded-full border border-slate-100 font-medium">{cn.category || "Client"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => document.getElementById("client-list-csv-upload").click()}
              className="w-full text-white text-xs font-bold py-2.5 rounded-xl uppercase tracking-wide flex items-center justify-center gap-2 shadow-sm"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = hoverColor}
              onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
            >
              <Upload className="w-3.5 h-3.5" /> Upload client list (CSV/JSON/Excel)
            </Button>
            <input 
              type="file" 
              accept=".csv,.json,.xlsx,.xls"
              className="hidden" 
              id="client-list-csv-upload"
              onChange={handleClientListUpload}
            />
          </div>
        </div>

        {/* Right column: Live URL integration */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
            <Link2 className="w-4 h-4" style={{ color: primaryColor }} />
            Live Document Linking
          </h3>
          
          {isLinked ? (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-3">
              <div className="flex justify-between items-start">
                <div className="overflow-hidden">
                  <p className="text-xs font-bold text-slate-800 truncate" title={linkedUrl}>{linkedUrl}</p>
                  <p className="text-[10px] text-slate-400 font-semibold mt-1">Status: Linked &amp; Connected</p>
                  <p className="text-[10px] text-slate-400 font-semibold">Last Synced: {lastSynced}</p>
                </div>
                <button 
                  onClick={handleUnlinkDocument}
                  className="text-[10px] font-bold text-red-500 hover:underline shrink-0 ml-2"
                >
                  Unlink
                </button>
              </div>
              <Button
                disabled={isSyncing}
                onClick={() => syncLinkedDocument(linkedUrl)}
                className="w-full text-xs font-bold py-2 rounded-xl uppercase tracking-wide border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 flex items-center justify-center gap-2 shadow-sm"
              >
                {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                <span>Sync Now</span>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleLinkDocument} className="space-y-2">
              <p className="text-[10px] text-slate-400 leading-relaxed font-semibold">
                Link to a live Google Sheet (published as CSV), online CSV, or Excel web url to automatically poll and check new entries.
              </p>
              <div className="flex gap-2">
                <Input 
                  value={linkInput}
                  onChange={e => setLinkInput(e.target.value)}
                  placeholder="Paste live document URL..."
                  className="text-xs rounded-xl bg-white focus-visible:ring-0 shadow-sm flex-grow"
                />
                <Button
                  type="submit"
                  className="text-white text-xs font-bold px-4 py-2.5 rounded-xl uppercase tracking-wide shadow-sm shrink-0"
                  style={{ backgroundColor: primaryColor }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = hoverColor}
                  onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                >
                  Link
                </Button>
              </div>
            </form>
          )}
        </div>

      </div>

      {/* Scanner Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-100/40 overflow-hidden print:hidden">
        
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5" style={{ color: primaryColor }} />
              Conflict Scanner
            </h2>
            <p className="text-xs text-slate-500 mt-1">Upload police briefs, file records, or fact sheets to extract names and verify conflicts.</p>
          </div>
          <button 
            onClick={handleConflictReset}
            className="text-xs font-bold text-slate-500 hover:text-red-500 transition-colors uppercase tracking-wider"
          >
            Clear Scanner
          </button>
        </div>

        {/* Upload Container */}
        <div className="p-8 space-y-6">
          {conflicts === null && (
            <div 
              className="border-2 border-dashed border-slate-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center space-y-4 hover:border-slate-300 transition-all cursor-pointer bg-slate-50/20"
              onClick={() => document.getElementById("standalone-conflict-upload").click()}
            >
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 group-hover:text-slate-600 transition-colors">
                <Upload className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">Upload document to check conflicts</p>
                <p className="text-xs text-slate-400 mt-1">PDF, DOCX, or Image (Police brief, fact sheet, etc.)</p>
              </div>
              <input 
                type="file" 
                multiple
                className="hidden" 
                id="standalone-conflict-upload"
                onChange={handleAddConflictFiles}
              />
              <Button 
                type="button"
                variant="outline"
                className="rounded-xl font-bold text-xs uppercase tracking-wide border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Choose Files
              </Button>
            </div>
          )}

          {/* Files List */}
          {conflictFiles.length > 0 && conflicts === null && (
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Files to Check ({conflictFiles.length})</h3>
              <div className="grid grid-cols-1 gap-2">
                {conflictFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-xl">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="text-xs font-bold text-slate-700 truncate">{file.name}</span>
                      <span className="text-[10px] text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button 
                      onClick={() => handleRemoveFile(idx)}
                      className="p-1 hover:bg-slate-200 rounded-lg text-slate-400 hover:text-red-500 transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Scan Action */}
              <button
                onClick={handleConflictScan}
                disabled={conflictLoading}
                className="w-full text-white py-3 rounded-xl text-xs font-bold uppercase tracking-wider disabled:bg-slate-200 disabled:text-slate-400 transition-all shadow-md flex items-center justify-center gap-2 mt-4"
                style={{ backgroundColor: conflictLoading ? "#e2e8f0" : primaryColor }}
                onMouseEnter={(e) => { if (!conflictLoading) e.target.style.backgroundColor = hoverColor; }}
                onMouseLeave={(e) => { if (!conflictLoading) e.target.style.backgroundColor = primaryColor; }}
              >
                {conflictLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{scanProgress || "Extracting Entities & Scanning..."}</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-4.5 h-4.5" />
                    <span>Run Multi-File Scan</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Scan Results */}
          {scanError && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3 mt-4 text-red-700">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold">Extraction Error</p>
                <p className="text-xs mt-1">{scanError}</p>
              </div>
            </div>
          )}

          {conflicts !== null && (
            <div className="space-y-6 animate-fade-in">
              
              {getActiveConflictsCount() === 0 ? (
                <div className="p-5 rounded-2xl bg-green-50 border border-green-100 flex items-start gap-3 text-green-800">
                  <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5 text-green-600" />
                  <div>
                    <h3 className="text-sm font-bold">No Conflict Found</h3>
                    <p className="text-xs text-green-600/95 mt-1">No matches were detected against the client list database, or matches were cleared by DOB verification.</p>
                  </div>
                </div>
              ) : (
                <div className="p-5 rounded-2xl bg-red-50 border border-red-100 flex items-start gap-3 text-red-900">
                  <AlertTriangle className="w-6 h-6 shrink-0 mt-0.5 text-red-600" />
                  <div>
                    <h3 className="text-sm font-bold">{getActiveConflictsCount()} Conflict matches detected</h3>
                    <p className="text-xs text-red-700 mt-1">One or more names extracted from the documents match records in your client list database and need verification.</p>
                  </div>
                </div>
              )}

              {/* Extracted Parties & Conflict Status Table */}
              {extractedNames && (
                <div className="space-y-4">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-2">Extracted Parties &amp; Conflict Status</h3>
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th scope="col" className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th scope="col" className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                            Role / Type
                          </th>
                          <th scope="col" className="px-6 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                            Conflict Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {getCombinedExtractedNames().length > 0 ? (
                          getCombinedExtractedNames().map((person, idx) => {
                            const conflict = getConflictForPerson(person.full_name);
                            return (
                              <tr key={idx} className="hover:bg-slate-50/50 transition-colors align-top">
                                <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                                  {person.full_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(person)}`}>
                                    {formatRole(person)}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  {conflict ? (
                                    <div className="space-y-3">
                                      {renderConflictStatus(person, conflict)}
                                      
                                      <div className="text-xs text-slate-600 pl-2.5 border-l-2 border-red-200 space-y-1 mt-1">
                                        <p className="font-semibold text-red-700 uppercase tracking-wider text-[10px]">Matches client list record:</p>
                                        {conflict.matches.map((m, mIdx) => (
                                          <div key={mIdx} className="flex items-center gap-1.5 text-slate-600 text-xs">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0"></span>
                                            <span>
                                              {m.name} {m.dob ? <span className="text-slate-400 font-medium">(DOB: {m.dob})</span> : ""} <span className="text-[10px] text-slate-400 font-medium bg-slate-100 border px-1.5 py-0.5 rounded-full">{m.category || "Client"}</span>
                                            </span>
                                          </div>
                                        ))}
                                      </div>

                                      {/* DOB Input box shown for all flagged conflicts */}
                                      <div className="mt-2.5 p-2.5 bg-slate-50 border border-slate-100 rounded-xl max-w-xs space-y-1.5">
                                        <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wide block">
                                          Enter Extracted Person's DOB:
                                        </label>
                                        <Input
                                          type="text"
                                          placeholder="e.g. DD/MM/YYYY or YYYY-MM-DD"
                                          value={enteredDobs[person.full_name] || ""}
                                          onChange={(e) => handleDobChange(person.full_name, e.target.value)}
                                          className="h-8 text-xs px-2.5 rounded-lg border-slate-200 focus-visible:ring-1 bg-white shadow-inner"
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                                      Clear / No Conflict
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="3" className="px-6 py-8 text-center text-xs text-slate-400 italic">
                              No parties extracted from document.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {scanCompletedAt && (
                    <div className="text-[11px] text-slate-400 font-semibold text-right mt-1.5 uppercase tracking-wider">
                      Scan Completed: {scanCompletedAt}
                    </div>
                  )}
                </div>
              )}
 
              {/* Print, Save, Reset Actions */}
              <div className="flex flex-wrap justify-between items-center pt-4 border-t border-slate-100 gap-3">
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    onClick={handlePrint}
                    variant="outline"
                    className="rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wide border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                  >
                    <Printer className="w-4 h-4" /> Print Check
                  </Button>
                    {/* A conflict check is evidence the check was run and what it found:
                        supporting material on the matter rather than funding paperwork. */}
                    <ActionstepSavePanel
                      formLabel="Conflict Check"
                      disabled={!extractedNames}
                      saveNote={false}
                      getDocuments={async () => {
                        const names = getCombinedExtractedNames();
                        const doc = createPortalPdf({ title: "Conflict Check", brandColor: "#B91C1C" });
                        doc.heading("Check");
                        doc.fields([
                          ["Completed", scanCompletedAt || new Date().toLocaleString("en-AU")],
                          ["Documents scanned", String(conflictFiles.length)],
                          ["Names found", String(names.length)],
                          ["Conflicts", conflicts?.length ? String(conflicts.length) : "None identified"],
                        ]);
                        if (conflictFiles.length) {
                          doc.heading("Documents scanned");
                          conflictFiles.forEach((file) => doc.bullet(file.name));
                        }
                        doc.heading("Names identified");
                        if (names.length) {
                          names.forEach((entry) => {
                            const label = [entry.name, entry.category].filter(Boolean).join(" - ");
                            doc.bullet(entry.dob ? `${label} (DOB ${entry.dob})` : label);
                          });
                        } else {
                          doc.body("No names were extracted.", { after: 1 });
                        }
                        doc.heading("Conflicts");
                        if (conflicts?.length) {
                          conflicts.forEach((conflict) =>
                            doc.bullet(typeof conflict === "string" ? conflict : conflict?.name || "Conflict"),
                          );
                        } else {
                          doc.body("No conflicts identified.", { after: 1 });
                        }
                        return [{
                          file: doc.toFile(portalDocumentName("Conflict-Check", "check")),
                          folder: actionstepFolderFor("supportMaterial"),
                        }];
                      }}
                    />
                  <Button 
                    onClick={handleDownloadReport}
                    variant="outline"
                    className="rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wide border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                  >
                    <Download className="w-4 h-4" /> Save to File
                  </Button>
                  <Button
                    onClick={() => {
                      setEmailTo(INTAKE_EMAIL);
                      setShowEmailDialog(true);
                    }}
                    variant="outline"
                    className="rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wide border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5"
                  >
                    <Mail className="w-4 h-4" /> Email Report
                  </Button>
                </div>
                <Button 
                  onClick={handleConflictReset}
                  className="rounded-xl px-6 py-2.5 font-bold text-white uppercase tracking-wider text-xs shadow-md"
                  style={{ backgroundColor: primaryColor }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = hoverColor}
                  onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                >
                  Start New Scan
                </Button>
              </div>
 
            </div>
          )}
 
        </div>
 
      </div>

      {/* Saved History Card */}
      {savedChecks.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 print:hidden">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2 mb-4">
            <History className="w-4.5 h-4.5" style={{ color: primaryColor }} />
            Saved Conflict Checks History
          </h3>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1 scrollbar-thin">
            {savedChecks.map((check) => (
              <div 
                key={check.id} 
                onClick={() => handleLoadSavedCheck(check)}
                className={`flex items-center justify-between p-3.5 hover:bg-slate-100/50 border rounded-xl transition-all cursor-pointer group ${
                  currentCheckId === check.id ? "bg-slate-100/70 border-slate-300 ring-1 ring-slate-200" : "bg-slate-50 border-slate-100"
                }`}
              >
                <div className="flex flex-col gap-1 min-w-0 pr-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-700 truncate max-w-[250px]" title={check.files.join(", ")}>
                      {check.files.join(", ")}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                      check.conflicts_count === 0 
                        ? "bg-green-50 text-green-700 border border-green-100" 
                        : "bg-red-50 text-red-700 border border-red-100"
                    }`}>
                      {check.conflicts_count === 0 ? "Clear" : `${check.conflicts_count} Conflict(s)`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400 font-semibold">
                    <span>{check.completed_at}</span>
                    <span>•</span>
                    <span>{check.extracted_count} names extracted</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleDeleteSavedCheck(check.id, e)}
                    className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Print View Report */}
      <div id="print-report-container" className="print-only text-black p-8 max-w-4xl mx-auto space-y-6" style={{ fontFamily: "Georgia, serif" }}>
        
        {/* Centered Logo Header */}
        <div className="flex flex-col items-center text-center pb-6 border-b-2" style={{ borderColor: primaryColor }}>
          {brandInfo.logo ? (
            <img src={brandInfo.logo} alt={brandInfo.name} className="h-16 object-contain mb-3" />
          ) : (
            <h1 className="text-2xl font-bold uppercase tracking-wider text-slate-800">{brandInfo.name}</h1>
          )}
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">
            Conflict of Interest Check Report
          </p>
        </div>

        {/* Files Checked */}
        <div className="space-y-1.5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">Checked Files</h3>
          <ul className="text-xs space-y-1 list-disc pl-4 font-mono text-slate-600">
            {conflictFiles.map((f, i) => (
              <li key={i}>{f.name} ({(f.size / 1024).toFixed(1)} KB)</li>
            ))}
          </ul>
        </div>

        {/* Scan Status */}
        <div className="p-4 border rounded-xl" style={{ borderColor: primaryColor, backgroundColor: getActiveConflictsCount() === 0 ? "#f0fdf4" : "#fef2f2" }}>
          <h3 className="text-sm font-bold text-slate-800">
            {getActiveConflictsCount() === 0 ? "NO CONFLICT FOUND" : "POTENTIAL CONFLICTS DETECTED"}
          </h3>
          <p className="text-xs text-slate-600 mt-1">
            {getActiveConflictsCount() === 0 
              ? "All names extracted from the documents have been checked against the active client database and are clear of conflicts." 
              : `${getActiveConflictsCount()} active match(es) detected against the active client list database.`}
          </p>
        </div>

        {/* Extracted Parties & Conflict Status Print Table */}
        {extractedNames && (
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider border-b pb-1" style={{ borderColor: primaryColor }}>
              Extracted Parties &amp; Conflict Status
            </h3>
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b bg-slate-50" style={{ borderColor: primaryColor }}>
                  <th className="py-2 px-3 font-bold text-slate-700">Name</th>
                  <th className="py-2 px-3 font-bold text-slate-700">Role / Type</th>
                  <th className="py-2 px-3 font-bold text-slate-700">Conflict Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {getCombinedExtractedNames().length > 0 ? (
                  getCombinedExtractedNames().map((person, idx) => {
                    const conflict = getConflictForPerson(person.full_name);
                    return (
                      <tr key={idx} className="align-top">
                        <td className="py-2.5 px-3 font-semibold text-slate-900">{person.full_name}</td>
                        <td className="py-2.5 px-3 text-slate-700">{formatRole(person)}</td>
                        <td className="py-2.5 px-3">
                          {renderPrintConflictStatus(person, conflict)}
                          {conflict && (
                            <div className="pl-2.5 border-l border-red-300 text-slate-600 text-[10px] space-y-0.5 mt-1">
                              <span className="font-semibold text-red-600">Matches:</span>
                              {conflict.matches.map((m, mIdx) => (
                                <div key={mIdx}>
                                  • {m.name} {m.dob ? `(DOB: ${m.dob})` : ""} ({m.category || "Client"})
                                </div>
                              ))}
                              {enteredDobs[person.full_name] && (
                                <div className="text-[10px] text-slate-500 font-semibold mt-1">
                                  Entered DOB: {enteredDobs[person.full_name]}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="py-4 text-center italic text-slate-400">
                      No parties extracted.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Completed Timestamp placed below the table */}
            <div className="pt-4 flex justify-between items-center text-[10px] text-slate-500 font-semibold border-t border-slate-200 mt-8">
              <span>Performed By: {brandInfo.name} Portal</span>
              <span>Completed Date &amp; Time: {scanCompletedAt || new Date().toLocaleString("en-AU")}</span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media print {
          body, html {
            background: white !important;
            color: black !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .print\\:hidden, header, nav, button, input, .shadow-xl, .border-slate-200 {
            display: none !important;
            box-shadow: none !important;
            border: none !important;
          }
          .print-only {
            display: block !important;
          }
          .bg-slate-50, .bg-green-50, .bg-red-50 {
            background-color: transparent !important;
            border: 1px solid #ddd !important;
          }
          #print-report-container {
            display: block !important;
            padding: 20px !important;
          }
        }
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>

      {/* Email Report Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-lg">
          <form onSubmit={handleSendEmail}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" style={{ color: primaryColor }} />
                <span>Email Conflict Check Report</span>
              </DialogTitle>
              <DialogDescription>
                Send a summary of this conflict check report via email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">To <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  required
                  placeholder="recipient@example.com"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white"
                  disabled={emailing}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">CC</label>
                <input
                  type="email"
                  placeholder="cc@example.com (optional)"
                  value={emailCc}
                  onChange={(e) => setEmailCc(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white"
                  disabled={emailing}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">Message</label>
                <textarea
                  placeholder="Add an optional message..."
                  value={emailMsg}
                  onChange={(e) => setEmailMsg(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y bg-white"
                  disabled={emailing}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEmailDialog(false)}
                className="rounded-xl font-bold"
                disabled={emailing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-xl text-white font-bold"
                style={{ backgroundColor: primaryColor }}
                disabled={emailing || !emailTo}
              >
                {emailing ? "Sending..." : "Send Email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
